import { Injectable } from '@angular/core';
import { isEqual, isNil } from 'lodash-es';
import { BehaviorSubject, type Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { type Row, type RowCheckboxState } from '../../table.interfaces';

/**
 * The service to manage the row selection.
 * It allows to select all rows, select one row.
 *
 * Each consumer must call the reset method to initialize the service.
 * Each consumer must handle its own instance of the service.
 */
@Injectable(/* legit non root */)
export class RowSelectionService<Key extends string> {
  /**
   * The observable to know if all rows are selected.
   * It is updated each time user selects one or multiple rows.
   *
   * Returns true if all rows editable rows are selected, false otherwise.
   */
  public get allSelected$(): Observable<boolean> {
    return this._allSelected$;
  }

  /**
   * The checkbox state for each row. Emit only when the value changes.
   */
  public get rowIdCheckboxStateMap$(): Observable<
    Map<string | number, RowCheckboxState>
  > {
    return this._rowIdCheckboxStateMap$.pipe(
      distinctUntilChanged((a, b) => isEqual(Array.from(a), Array.from(b))),
    );
  }

  /**
   * Get a copy of the current rowIdCheckboxStateMap.
   */
  public getRowIdCheckboxStateMapSnapshot(): Map<
    string | number,
    RowCheckboxState
  > {
    return new Map(this._rowIdCheckboxStateMap$.getValue());
  }

  public reset(rows: Row<Key>[]): void {
    this._rowIdCheckboxStateMap$.next(
      new Map<string | number, RowCheckboxState>(),
    );

    this._inputDisabledByRowId = new Map(
      rows.map(row => [row.id, !!row.isDisabled]),
    );

    this._updateRowCheckboxStates(rows);
  }

  /**
   * Adopt the input checkbox state of the rows whose isDisabled changed since the previous input,
   * keeping the other selections.
   */
  public syncDisabledRows(rows: Row<Key>[]): void {
    const changedRows = rows.filter(
      row =>
        row.checkboxState &&
        this._inputDisabledByRowId.has(row.id) &&
        this._inputDisabledByRowId.get(row.id) !== !!row.isDisabled,
    );

    if (!changedRows.length) {
      return;
    }

    const rowIdCheckboxStateMap = this.getRowIdCheckboxStateMapSnapshot();

    for (const row of changedRows) {
      rowIdCheckboxStateMap.set(row.id, row.checkboxState);
      this._inputDisabledByRowId.set(row.id, !!row.isDisabled);
    }

    this._emitEvents(rowIdCheckboxStateMap);
  }

  /**
   * Update all rows checkbox states.
   * If a row is readonly, it is not updated.
   */
  public selectAll(rows: Row<Key>[]): void {
    this._updateRowCheckboxStates(rows, !this._allSelected$.getValue());
  }

  /**
   * Update one row checkbox state.
   * If the row is readonly, the current checked state is returned.
   * If the row has an onCheckboxClick callback, the result of the callback is passed as strictValue and used as the new checked state.
   * Otherwise, reverse the current checked state.
   *
   * Also, if the row's checkboxState has an afterChanged callback, it is called after all changes are made.
   * Its changes to a disabled row are ignored.
   */
  public selectOne(row: Row<Key>, strictValue: boolean): void {
    this._updateRowCheckboxStates([row], strictValue);
    this._handleAfterChanged(row);
  }

  private _rowIdCheckboxStateMap$ = new BehaviorSubject<
    Map<string | number, RowCheckboxState>
  >(new Map());

  private _allSelected$ = new BehaviorSubject<boolean>(null);

  private _inputDisabledByRowId = new Map<string | number, boolean>();

  private _handleAfterChanged(row: Row<Key>): void {
    if (
      row.checkboxState.afterChanged &&
      typeof row.checkboxState.afterChanged === 'function'
    ) {
      const rowIdCheckboxStateMap = this.getRowIdCheckboxStateMapSnapshot();

      const changes = row.checkboxState.afterChanged(
        row.checkboxState.checked,
        rowIdCheckboxStateMap,
      );

      if (changes instanceof Map) {
        for (const [rowId, checkboxState] of changes) {
          if (this._inputDisabledByRowId.get(rowId)) {
            continue;
          }

          rowIdCheckboxStateMap.set(rowId, checkboxState);
        }

        this._emitEvents(rowIdCheckboxStateMap);
      }
    }
  }

  /**
   * Get the next checked state for single row
   *
   * If the row is readonly, the current checked state is returned.
   * If the row has an onCheckboxClick callback, the result of the callback is returned.
   * Otherwise, the nextCheckedState is returned.
   */
  private _getNewCheckedStateForRow(
    row: Row<any>,
    strictValue: boolean,
  ): boolean {
    const { checked: currentCheckedState } = row.checkboxState;

    if (row.checkboxState.readonly) {
      return currentCheckedState;
    }

    if (!isNil(strictValue)) {
      return strictValue;
    }

    return !currentCheckedState;
  }

  private _updateRowCheckboxStates(
    rows: Row<any>[],
    strictValue?: boolean,
  ): void {
    const rowIdCheckboxStateMap = this.getRowIdCheckboxStateMapSnapshot();

    for (const row of rows) {
      const storedCheckboxState = rowIdCheckboxStateMap.get(row.id);

      if (!storedCheckboxState) {
        rowIdCheckboxStateMap.set(row.id, row.checkboxState);

        continue;
      }

      const newCheckedState = this._getNewCheckedStateForRow(row, strictValue);

      if (storedCheckboxState.checked === newCheckedState) {
        continue;
      }

      rowIdCheckboxStateMap.set(row.id, {
        ...storedCheckboxState,
        checked: newCheckedState,
      });
    }

    this._emitEvents(rowIdCheckboxStateMap);
  }

  private _getSelectAllFromRowCheckboxStateMap(
    rowIdCheckboxStateMap: Map<string | number, RowCheckboxState>,
  ): boolean {
    const rowIdCheckboxStateArray = Array.from(rowIdCheckboxStateMap.values());

    return rowIdCheckboxStateArray.length
      ? rowIdCheckboxStateArray
          .filter(rowCheckboxState => !rowCheckboxState.readonly)
          .every(rowCheckboxState => rowCheckboxState.checked)
      : false;
  }

  private _emitEvents(values: Map<string | number, RowCheckboxState>): void {
    this._allSelected$.next(this._getSelectAllFromRowCheckboxStateMap(values));

    this._rowIdCheckboxStateMap$.next(values);
  }
}
